'use client';

import { useCallback } from 'react';
import {
  useAppDispatch,
  useAppSelector,
  setUserSetting,
  setUserSettings,
  removeUserSetting,
  clearUserSettings,
  selectUserSettings,
  selectIsHydrated,
} from 'src/redux';

/**
 * Return type for useUserSettings hook
 */
export interface UseUserSettingsReturn {
  // State
  settings: Record<string, any>;
  isHydrated: boolean;

  // Actions
  getSetting: <T>(key: string) => T | undefined;
  setSetting: <T>(key: string, value: T) => void;
  updateSettings: (settings: Record<string, any>) => void;
  removeSetting: (key: string) => void;
  clearAllSettings: () => void;
}

/**
 * Custom hook for managing user settings with Redux.
 * Provides a clean interface for components without exposing Redux internals.
 *
 * @returns {UseUserSettingsReturn} Object containing settings state and management functions
 * @example
 * const {
 *   settings,
 *   getSetting,
 *   setSetting,
 *   updateSettings
 * } = useUserSettings();
 *
 * // Get a setting
 * const currentLocation = getSetting<string>('currentLocation');
 *
 * // Set a setting
 * setSetting('currentLocation', 'discovery');
 *
 * // Update multiple settings
 * updateSettings({ currentLocation: 'discovery', theme: 'dark' });
 */
const useUserSettings = (): UseUserSettingsReturn => {
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectUserSettings);
  const isHydrated = useAppSelector(selectIsHydrated);

  /**
   * Get a specific setting by key
   * @param key - The setting key
   * @returns The setting value or undefined
   */
  const getSetting = useCallback(
    <T>(key: string): T | undefined => {
      return settings[key] as T | undefined;
    },
    [settings]
  );

  /**
   * Set a specific setting by key
   * @param key - The setting key
   * @param value - The setting value
   */
  const setSetting = useCallback(
    <T>(key: string, value: T) => {
      dispatch(setUserSetting({ key, value }));
    },
    [dispatch]
  );

  /**
   * Update multiple settings at once
   * @param newSettings - Object containing settings to update
   */
  const updateSettings = useCallback(
    (newSettings: Record<string, any>) => {
      dispatch(setUserSettings(newSettings));
    },
    [dispatch]
  );

  /**
   * Remove a specific setting by key
   * @param key - The setting key to remove
   */
  const removeSetting = useCallback(
    (key: string) => {
      dispatch(removeUserSetting(key));
    },
    [dispatch]
  );

  /**
   * Clear all user settings
   */
  const clearAllSettings = useCallback(() => {
    dispatch(clearUserSettings());
  }, [dispatch]);

  return {
    settings,
    isHydrated,
    getSetting,
    setSetting,
    updateSettings,
    removeSetting,
    clearAllSettings,
  };
};

export default useUserSettings;
